import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  listSessionUsers,
  listUserSessions,
  type AdminSessionSummary,
  type AdminSessionUsersPage,
} from "../../../services/adminSessionsService";
import UserList from "./UserList";
import UserSessionList from "./UserSessionList";
import { parseSessionsState, sessionsSearch, type SessionsTabState } from "./adminSessionsUrl";

export interface SessionsTabProps {
  getToken: () => string | undefined;
}

/**
 * Admin → Sessions: users with sessions on the left, the selected user's
 * sessions on the right. All state lives in the URL so returning from a
 * session page lands back on the same user, page and search.
 */
const SessionsTab: React.FC<SessionsTabProps> = ({ getToken }) => {
  const navigate = useNavigate();
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const state = parseSessionsState(searchParams);
  // The prop is a new function on every parent render, so depending on it
  // would refetch on every navigation.
  const getTokenRef = useRef(getToken);
  getTokenRef.current = getToken;

  const [usersPage, setUsersPage] = useState<AdminSessionUsersPage | null>(null);
  const [usersLoading, setUsersLoading] = useState(true);
  const [usersError, setUsersError] = useState<string | null>(null);

  const [sessions, setSessions] = useState<AdminSessionSummary[] | null>(null);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  const update = useCallback(
    (next: Partial<SessionsTabState>) =>
      setSearchParams(
        new URLSearchParams(
          sessionsSearch({ ...parseSessionsState(searchParams), ...next }),
        ),
      ),
    [searchParams, setSearchParams],
  );

  useEffect(() => {
    const controller = new AbortController();
    setUsersLoading(true);
    setUsersError(null);
    listSessionUsers({ page: state.page, search: state.q }, getTokenRef.current(), controller.signal)
      .then(setUsersPage)
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setUsersError(err instanceof Error ? err.message : "Could not load users.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setUsersLoading(false);
      });
    return () => controller.abort();
  }, [state.page, state.q]);

  useEffect(() => {
    setSessions(null);
    setSessionsError(null);
    if (!state.user) return;
    const controller = new AbortController();
    setSessionsLoading(true);
    listUserSessions(state.user, getTokenRef.current(), controller.signal)
      .then(setSessions)
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setSessionsError(err instanceof Error ? err.message : "Could not load sessions.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setSessionsLoading(false);
      });
    return () => controller.abort();
  }, [state.user]);

  const selected = usersPage?.users.find((u) => u.userId === state.user);
  // The selected user may sit on a different page of the list; fall back to
  // a generic heading rather than hiding their sessions.
  const heading = state.user
    ? (selected?.email ?? selected?.stytchMemberId ?? "Selected user")
    : null;

  const onSearch = useCallback((q: string) => update({ q, page: 1 }), [update]);

  return (
    <div className="grid grid-cols-1 md:grid-cols-[20rem_minmax(0,1fr)] gap-4">
      <UserList
        data={usersPage}
        loading={usersLoading}
        error={usersError}
        query={state.q}
        selectedUserId={state.user}
        onSearch={onSearch}
        onPage={(page) => update({ page })}
        onSelect={(user) => update({ user })}
      />
      <UserSessionList
        heading={heading}
        sessions={sessions}
        loading={sessionsLoading}
        error={sessionsError}
        onOpen={(sessionId) =>
          navigate(`/${orgSlug}/admin/sessions/${sessionId}`, {
            state: { from: sessionsSearch(state) },
          })
        }
      />
    </div>
  );
};

export default SessionsTab;
